<script setup lang="ts">
/**
 * **小地图** —— `<MiniMap>` 的替代品（我们自己的画布）。
 *
 * ## 它必须暴露给宿主的三个几何事实
 *
 * 宿主的 `useMiniMapGesture` **从 DOM 读**（不是从 props）：
 *
 * ```text
 *   svg.viewBox.baseVal          → 画布坐标下的取景框
 *   svg.getBoundingClientRect()  → 元素实际像素尺寸
 * ```
 *
 * 两者一起才能换算"手指在小地图上拖了 10px，等于画布上多少单位"
 * （`minimapGesture.unitsPerPx`）。所以这里的 `viewBox` **必须是画布坐标**，
 * 而不是"把内容缩放到 0..200"那种局部坐标 —— 后者会让拖动手势方向对、
 * 距离差一个系数，而且**只有拖起来才发现**。
 *
 * ## 取景框怎么定
 *
 * 内容边界（所有节点的并集）与**当前视口**一起装进去，再按元素宽高比
 * 撑成同比例 —— 不这么做的话，节点少时小地图里的东西会贴边、看不清。
 *
 * ⚠️ 用一个**稳定**的取景框：它随内容和视口变化，但不做动画。
 * 每帧重算并写 `viewBox` 是廉价的（一个属性），而做平滑插值就要写
 * 动画循环 —— 小地图不值得。
 */
import { computed } from 'vue'
import type { Viewport } from '../minimapGesture'
import type { FlowNode } from '../flowTypes'
import type { Size } from './handleGeometry'

const props = withDefaults(
  defineProps<{
    viewport: Viewport
    /** 全部节点（位置 + 量出来的尺寸；没量到的按 0 算，和 `fitView` 一致） */
    nodes: readonly FlowNode[]
    dimensionsOf: (id: string) => Size | undefined
    /**
     * **画布容器**的尺寸（不是小地图自己的）。
     *
     * ⚠️ 视口矩形 = 容器尺寸 ÷ zoom —— 用小地图的尺寸算会得到一个
     * 完全无关的框（"小地图里的可视范围"会永远等于它自己那么大）。
     */
    canvasSize: Size
    width?: number
    height?: number
    /** 每个节点的填充色（按类别配色 —— 扫一眼看得出结构） */
    nodeColor?: (node: FlowNode) => string
    nodeStroke?: (node: FlowNode) => string
    /** 遮罩色（视口之外压暗） */
    maskColor?: string
  }>(),
  {
    width: 200,
    height: 130,
    nodeColor: () => 'var(--np-minimap-node, #6b7280)',
    nodeStroke: () => 'var(--np-minimap-node-stroke, rgba(0,0,0,0.5))',
    maskColor: 'var(--np-minimap-mask, rgba(0,0,0,0.55))',
  },
)

/** 视口在画布坐标里占的矩形（= 屏幕矩形 ÷ zoom，再挪到 -x/-y） */
const viewBoxRect = computed(() => {
  const { x, y, zoom } = props.viewport
  const size = props.canvasSize
  return {
    x: -x / zoom,
    y: -y / zoom,
    width: Math.max(1, size.width / zoom),
    height: Math.max(1, size.height / zoom),
  }
})

/** 节点盒（画布坐标） */
const boxes = computed(() =>
  props.nodes.map((node) => {
    const size = props.dimensionsOf(node.id)
    const measured = (size?.width ?? 0) > 0 && (size?.height ?? 0) > 0
    const fallback = fallbackSize(node)
    return {
      id: node.id,
      x: node.position.x,
      y: node.position.y,
      /*
       * ⚠️ 还没量到尺寸的节点给一个**按模型估的块**，而不是 0。
       * 0 会画成 1px —— 真机反馈的「小地图里的节点是一个个方块」
       * 就是这么来的（尺寸错了之后整片地图全是小方块）。
       * 现在：宽度用模型声明的宽度（准的），高度按行数估（只是占位，
       * 量到就覆盖）—— 至少比例看起来是对的。
       */
      width: measured ? size!.width : fallback.width,
      height: measured ? size!.height : fallback.height,
      measured,
      node,
    }
  }),
)

/** 没测到尺寸时的占位块：宽度取模型声明，高度按行数粗估 */
function fallbackSize(node: FlowNode): { width: number; height: number } {
  const model = node.data?.model as { width?: number; rows?: unknown[] } | undefined
  const width = typeof model?.width === 'number' && model.width > 0 ? model.width : UNMEASURED.width
  const rows = Array.isArray(model?.rows) ? model!.rows!.length : 3
  return { width, height: Math.max(40, 26 + rows * 28) }
}

/** 兜底值：模型里连宽度都没有时用（和 `types/node.ts` 的默认宽度同量级） */
const UNMEASURED = { width: 180, height: 90 }

/**
 * 取景框：内容 ∪ 视口，再留 10% 边距。
 *
 * ⚠️ 一个节点都没有时用**视口本身**当内容 —— 否则 `viewBox` 宽高为 0，
 * SVG 会退化成不渲染（空白一块），看着像"小地图坏了"。
 */
const frame = computed(() => {
  const list = [...boxes.value.map((b) => ({ x: b.x, y: b.y, width: b.width, height: b.height })), viewBoxRect.value]
  const left = Math.min(...list.map((b) => b.x))
  const top = Math.min(...list.map((b) => b.y))
  const right = Math.max(...list.map((b) => b.x + b.width))
  const bottom = Math.max(...list.map((b) => b.y + b.height))
  const padX = Math.max(20, (right - left) * 0.1)
  const padY = Math.max(20, (bottom - top) * 0.1)
  return {
    x: left - padX,
    y: top - padY,
    width: Math.max(1, right - left + padX * 2),
    height: Math.max(1, bottom - top + padY * 2),
  }
})

const viewBox = computed(
  () => `${frame.value.x} ${frame.value.y} ${frame.value.width} ${frame.value.height}`,
)

/**
 * 遮罩：视口之外压暗。
 *
 * ⚠️ 用 `fill-rule="evenodd"` 的**一个** path，而不是四条矩形拼边框 ——
 * 后者在视口超出内容边界时会出现"接缝"（亚像素），而这里只画一次。
 */
const maskPath = computed(() => {
  const f = frame.value
  const v = viewBoxRect.value
  return (
    `M ${f.x} ${f.y} H ${f.x + f.width} V ${f.y + f.height} H ${f.x} Z ` +
    `M ${v.x} ${v.y} H ${v.x + v.width} V ${v.y + v.height} H ${v.x} Z`
  )
})

/*
 * ⚠️ **拖动必须捕获指针**（真机反馈「小地图无法操作」的一半原因就是它）。
 *
 * 拖动小地图 = 平移画布 = 地图里的内容跟着动，所以指针**很容易**滑出
 * 小地图那一小块 —— 没捕获的话，`pointermove` 从那一刻起就不再打到 svg 上，
 * 宿主的手势 handler 收不到事件，表现就是"拖两下就断"。
 *
 * 捕获放在这里（而不是宿主的手势代码里）：它是"这个控件收下了这次拖动"
 * 这件事的**组件级**表达，和谁消费事件无关。
 */
function onPointerDownCapture(ev: PointerEvent): void {
  ;(ev.currentTarget as Element | null)?.setPointerCapture?.(ev.pointerId)
}

function onPointerUpCapture(ev: PointerEvent): void {
  ;(ev.currentTarget as Element | null)?.releasePointerCapture?.(ev.pointerId)
}
</script>

<template>
  <!--
    ⚠️ 尺寸走 **attribute**（不是 CSS）：宿主的坐标换算读的是
    `getBoundingClientRect()`，而 CSS 的 width/height 一旦和 attribute
    不一致（比如被 max-width 压过），换算就错了 —— 症状是"拖动方向对、
    距离不对"。这里两者由同一对 props 决定。
  -->
  <svg
    class="np-graph__minimap nodrag nopan nowheel"
    :width="width"
    :height="height"
    :viewBox="viewBox"
    :style="{ width: `${width}px`, height: `${height}px` }"
    @pointerdown="onPointerDownCapture"
    @pointerup="onPointerUpCapture"
    @pointercancel="onPointerUpCapture"
  >
    <path class="np-graph__minimap-mask" :d="maskPath" :fill="maskColor" fill-rule="evenodd" />
    <rect
      v-for="box in boxes"
      :key="box.id"
      class="np-graph__minimap-node"
      :class="{ 'is-unmeasured': !box.measured }"
      :x="box.x"
      :y="box.y"
      :width="Math.max(1, box.width)"
      :height="Math.max(1, box.height)"
      rx="2"
      ry="2"
      :fill="nodeColor(box.node)"
      :stroke="nodeStroke(box.node)"
    />
    <!--
      ⚠️ 视口框**单独画一条描边**（不只是靠遮罩的洞）。
      遮罩只说明"外面被压暗"，而"这块可以拖"这件事得看得见 ——
      真机反馈「小地图无法拖拽」时，用户其实也不知道该拖哪儿。
    -->
    <rect
      class="np-graph__minimap-viewport"
      :x="viewBoxRect.x"
      :y="viewBoxRect.y"
      :width="Math.max(1, viewBoxRect.width)"
      :height="Math.max(1, viewBoxRect.height)"
      rx="2"
      ry="2"
    />
  </svg>
</template>
